// Pure translation layer for the task engine. No jsPsych, no DOM: this is the
// part that interprets a JOLIVE task definition the same way the desktop
// TaskRunner does, so it can be unit-tested in isolation.
//
// Two jobs:
//   1. assemble a block's trial list from its stimulus pool (the 3 modes the
//      desktop _start_next_block uses: pre-sequenced, weighted, standard)
//   2. compute the per-trial result row, byte-compatible with what the desktop
//      emits and the backend's log_task_trial stores (the parity anchor)

export type Rng = () => number; // [0, 1)

export interface Phase {
  type: string; // fixation | stimulus | iti | blank | feedback | ...
  duration_ms?: number;
  timeout_ms?: number;
  await_response?: boolean;
  size?: number;
  thickness?: number;
  color?: string;
}

export type Stim = Record<string, unknown> & {
  type?: string;
  text?: string;
  color?: string;
  font_size?: number;
  condition?: string;
  correct_response?: string;
};

export interface Block {
  label?: string;
  num_trials?: number;
  stimuli?: Stim[];
  pre_sequenced?: boolean;
  condition_weights?: Record<string, number>;
  randomize?: boolean;
  show_feedback?: boolean;
  feedback_duration_ms?: number;
  break_after_trials?: number;
  instructions?: string;
}

export interface TaskDefinition {
  task_name?: string;
  task_type?: string;
  instructions?: string;
  settings?: Record<string, unknown>;
  response_keys?: Record<string, string>; // key -> label
  trial_template?: Phase[];
  stimuli?: Stim[];
  blocks?: Block[];
}

export interface TrialPlan {
  trialNumber: number; // 1-based, global across blocks
  blockIndex: number;
  blockLabel: string;
  condition: string;
  stimulus: Stim;
  correctResponse: string;
  showFeedback: boolean;
  feedbackDurationMs?: number; // block-level override; renderer falls back to the phase duration
}

export interface BlockPlan {
  index: number;
  label: string;
  instructions?: string;
  breakAfterTrials?: number;
  trials: TrialPlan[];
}

// The result row. Shapes/null-handling mirror the desktop _end_trial exactly so
// online and in-lab data are identical.
export interface ResultTrial {
  trial_number: number;
  block_index: number;
  block_label: string;
  condition: string;
  stimulus_data: Record<string, unknown>;
  correct_response: string;
  actual_response: string | null;
  response_key: string | null;
  accuracy: number | null;
  rt_ms: number | null;
  timed_out: boolean;
}

export interface Captured {
  key: string;
  rt: number; // ms
}

function shuffle<T>(arr: T[], rng: Rng): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Round half-to-even, matching Python's built-in round() (banker's rounding),
// so weighted per-condition trial counts equal the desktop's. JS Math.round is
// half-up, which would diverge on exact .5 ties (e.g. 2.5 -> 3 vs Python's 2).
export function pyRound(x: number): number {
  const r = Math.round(x);
  if (Math.abs(r - x) === 0.5) return 2 * Math.round(x / 2); // exact tie -> to even
  return r;
}

// Mirrors desktop _start_next_block: pre-sequenced | weighted | standard.
export function assembleTrialList(block: Block, sharedStimuli: Stim[], rng: Rng): Stim[] {
  const source = block.stimuli && block.stimuli.length ? block.stimuli : sharedStimuli;
  if (!source || source.length === 0) return [];
  const numTrials = block.num_trials ?? source.length;

  if (block.pre_sequenced) {
    return source.slice();
  }

  if (block.condition_weights) {
    const weights = block.condition_weights;
    const total = Object.values(weights).reduce((s, v) => s + v, 0) || 1;
    const byCondition: Record<string, Stim[]> = {};
    for (const stim of source) {
      const cond = (stim.condition as string) ?? "";
      (byCondition[cond] ||= []).push(stim);
    }
    let list: Stim[] = [];
    for (const [cond, pct] of Object.entries(weights)) {
      const pool = byCondition[cond] ?? [];
      if (pool.length === 0) continue;
      const count = pyRound((numTrials * pct) / total);
      const reps = Math.max(1, Math.ceil(count / pool.length));
      const repeated: Stim[] = [];
      for (let r = 0; r < reps; r++) repeated.push(...pool);
      list = list.concat(repeated.slice(0, count));
    }
    return shuffle(list, rng);
  }

  // Standard pool-and-repeat
  const reps = Math.max(1, Math.ceil(numTrials / source.length));
  let pool: Stim[] = [];
  for (let r = 0; r < reps; r++) pool.push(...source);
  if (block.randomize ?? true) pool = shuffle(pool, rng);
  return pool.slice(0, numTrials);
}

export function hasResponsePhase(def: TaskDefinition): boolean {
  return (def.trial_template ?? []).some((p) => p.await_response === true);
}

// The RT zero-point rule, extracted so it is explicit and testable. The reaction
// time is measured from the STIMULUS onset: a stimulus phase anchors onset to its
// own load time, and that value is held for the rest of the trial (so n-back's
// two-window trial measures the gap response from the stimulus, not the gap).
//
// `onset === 0` is the "unset" sentinel. The trialNumber guard makes the rule
// per-trial: a new trial never inherits the previous trial's onset, so a malformed
// template (a response window with no preceding stimulus) can at worst anchor to
// itself within its own trial — never leak a stale onset across trials. For all 8
// shipped tasks (where every trial has a stimulus phase before its response
// window) this returns exactly what the previous inline logic did.
export function anchorOnset(p: {
  isStimulus: boolean;
  isAwait: boolean;
  now: number;
  onset: number;
  trialNumber: number;
  lastTrialNumber: number;
}): number {
  // New trial: treat onset as unset so it can't be inherited from the prior trial.
  const onset = p.trialNumber !== p.lastTrialNumber ? 0 : p.onset;
  if (p.isStimulus) return p.now; // the zero-point: the stimulus' own load time
  if (p.isAwait && onset === 0) return p.now; // response window with no stimulus yet
  return onset; // fixation/cue/iti/gap: keep the current onset unchanged
}

// Build the ordered block/trial plan for the whole task.
export function flattenTask(def: TaskDefinition, rng: Rng = Math.random): BlockPlan[] {
  const shared = def.stimuli ?? [];
  const blocks = def.blocks ?? [];
  const out: BlockPlan[] = [];
  let globalTrial = 0;

  blocks.forEach((block, blockIndex) => {
    const trialList = assembleTrialList(block, shared, rng);
    const label = block.label ?? `block_${blockIndex}`;
    const showFeedback = Boolean(block.show_feedback);
    const feedbackOverride = block.feedback_duration_ms;

    const trials: TrialPlan[] = trialList.map((stim) => {
      globalTrial += 1;
      return {
        trialNumber: globalTrial,
        blockIndex,
        blockLabel: label,
        condition: (stim.condition as string) ?? "",
        stimulus: stim,
        correctResponse: (stim.correct_response as string) ?? "",
        showFeedback,
        ...(feedbackOverride !== undefined ? { feedbackDurationMs: feedbackOverride } : {}),
      };
    });

    out.push({
      index: blockIndex,
      label,
      trials,
      ...(block.instructions !== undefined ? { instructions: block.instructions } : {}),
      ...(block.break_after_trials !== undefined
        ? { breakAfterTrials: block.break_after_trials }
        : {}),
    });
  });

  return out;
}

function stimulusData(stim: Stim): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(stim)) {
    if (k === "condition" || k === "correct_response" || k === "type") continue;
    out[k] = v;
  }
  return out;
}

// Mirrors desktop _end_trial. responseKeys maps key -> label.
export function computeTrialResult(
  plan: TrialPlan,
  captured: Captured | undefined,
  responseKeys: Record<string, string>,
  trialHasResponsePhase: boolean,
): ResultTrial {
  let correctLabel = plan.correctResponse;
  // If correct_response was given as a key name, resolve it to its label.
  if (correctLabel in responseKeys) correctLabel = responseKeys[correctLabel];

  const actualLabel = captured ? responseKeys[captured.key] ?? "" : null;

  let accuracy: number | null;
  if (correctLabel === "no_response" || correctLabel === "") {
    accuracy = captured ? 0 : 1; // go/no-go: correct = no response
  } else if (captured && actualLabel !== null) {
    accuracy = actualLabel === correctLabel ? 1 : 0;
  } else {
    accuracy = 0;
  }

  return {
    trial_number: plan.trialNumber,
    block_index: plan.blockIndex,
    block_label: plan.blockLabel,
    condition: plan.condition,
    stimulus_data: stimulusData(plan.stimulus),
    correct_response: plan.correctResponse,
    actual_response: actualLabel,
    response_key: captured ? captured.key : null,
    accuracy: trialHasResponsePhase ? accuracy : null,
    rt_ms: captured ? Math.round(captured.rt * 100) / 100 : null,
    timed_out: trialHasResponsePhase ? !captured : false,
  };
}

// --- Key name mapping -------------------------------------------------------
// JOLIVE response_keys use names like "space", "enter". The browser/jsPsych use
// KeyboardEvent.key values (" " for space, "Enter", etc.). These translate
// between the two so a definition that says {"space": "go"} works online and
// the stored response_key is the JOLIVE name (matching the desktop).

const JOLIVE_TO_JSPSYCH: Record<string, string> = {
  space: " ",
  enter: "Enter",
  tab: "Tab",
  backspace: "Backspace",
  escape: "Escape",
};

const JSPSYCH_TO_JOLIVE: Record<string, string> = {
  " ": "space",
  Enter: "enter",
  Tab: "tab",
  Backspace: "backspace",
  Escape: "escape",
};

// JOLIVE key name -> the choice string jsPsych expects.
export function joliveKeyToJsPsych(name: string): string {
  return JOLIVE_TO_JSPSYCH[name] ?? name;
}

// A key reported by jsPsych -> the JOLIVE key name (to look up the label and to
// store as response_key). Single characters are lowercased so "F" resolves to
// the "f" mapping, matching jsPsych's case-insensitive matching.
export function jsPsychKeyToJolive(key: string): string {
  if (key in JSPSYCH_TO_JOLIVE) return JSPSYCH_TO_JOLIVE[key];
  return key.length === 1 ? key.toLowerCase() : key;
}

// SART and similar vary stimulus size per presentation via font_size_variation
// (a pool of sizes). Mirrors the desktop random.choice(size_pool); falls back to
// the fixed font_size.
export function pickFontSize(stim: Stim, rng: Rng = Math.random): number {
  const pool = stim.font_size_variation;
  if (Array.isArray(pool) && pool.length > 0) {
    const i = Math.floor(rng() * pool.length);
    return Number(pool[i]);
  }
  return (stim.font_size as number) ?? 72;
}
